import type { RideDef } from '@runkr/config'
import type { EventSink } from '../events'
import { bodiesOverlap, centerX } from '../geom'
import { hit } from '../items/hit'
import type { World } from '../world'
import { activeRide } from './rides'

/**
 * Riders acting on other racers (D-050, after terrain in the step): a Thundertoad landing sends
 * a shockwave that stuns racers within `shockRadiusPx` (x distance between centres) and
 * `shockTierPx` (feet height), and a Wobblebot or Crabwalker bumps each racer it overlaps once per
 * ride. Both go through `hit()` with source `ride` and the rider as `attacker`, so shields pop
 * and other riders are immune. By player index; `wasGrounded` is each player before terrain.
 */
export function stepRideContacts(
  world: World,
  wasGrounded: readonly boolean[],
  events: EventSink,
): void {
  for (let i = 0; i < world.players.length; i++) {
    const p = world.players[i]!
    const ride = activeRide(world, p)
    if (!ride) continue
    if (ride.ability === 'shockwave' && p.grounded && !wasGrounded[i]) shock(world, i, ride, events)
    if (ride.bumpStunTicks > 0) bump(world, i, ride, events)
  }
}

function shock(world: World, i: number, ride: RideDef, events: EventSink): void {
  const p = world.players[i]!
  const tier = world.cfg.rides.shockTierPx
  for (let k = 0; k < world.players.length; k++) {
    const q = world.players[k]!
    if (k === i || Math.abs(centerX(q) - centerX(p)) > ride.shockRadiusPx) continue
    if (Math.abs(q.y - p.y) > tier) continue
    hit(world, k, 'ride', ride.shockStunTicks, events, i)
  }
}

function bump(world: World, i: number, ride: RideDef, events: EventSink): void {
  const p = world.players[i]!
  for (let k = 0; k < world.players.length; k++) {
    const bit = 1 << k
    if (k === i || (p.bumpedMask & bit) !== 0 || !bodiesOverlap(p, world.players[k]!)) continue
    if (hit(world, k, 'ride', ride.bumpStunTicks, events, i)) p.bumpedMask |= bit
  }
}
